// components/FunFact/index.jsx
import styled from 'styled-components';
import { motion, useAnimation } from 'framer-motion';
import { useState, useEffect } from 'react';

const FunFactSection = styled.section`
  padding: 3rem 2rem;
  background: ${({ theme }) => theme.backgroundAlt};
  position: relative;
  overflow: hidden;
  
  &::before, &::after {
    content: '';
    position: absolute;
    width: 400px;
    height: 400px;
    border-radius: 50%;
    background: linear-gradient(45deg, 
      ${({ theme }) => theme.primary}20,
      ${({ theme }) => theme.secondary}20
    );
    filter: blur(100px);
    animation: pulse 8s ease-in-out infinite alternate;
  }

  &::before {
    top: -200px;
    left: -200px;
  }

  &::after {
    bottom: -200px;
    right: -200px;
    animation-delay: -4s;
  }

  @keyframes pulse {
    0% { transform: scale(1); opacity: 0.3; }
    100% { transform: scale(1.2); opacity: 0.1; }
  }
`;

const SectionTitle = styled(motion.div)`
  text-align: center;
  margin-bottom: 3rem;
  position: relative;
  
  h2 {
    ${({ theme }) => theme.mixins.headingStyles};
    font-size: ${({ theme }) => theme.sizes['2xl']};
    background: linear-gradient(
      135deg,
      ${({ theme }) => theme.primary},
      ${({ theme }) => theme.secondary}
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    display: inline-block;
    padding: 0.5rem 2rem;
    position: relative;
    transition: all 0.3s ease;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      padding: 2px;
      border-radius: 30px;
      background: linear-gradient(
        135deg,
        ${({ theme }) => theme.primary},
        ${({ theme }) => theme.secondary}
      );
      -webkit-mask: 
        linear-gradient(#fff 0 0) content-box, 
        linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      opacity: 0.5;
      transition: opacity 0.3s ease;
    }

    &:hover {
      transform: scale(1.05);
      
      &::before {
        opacity: 1;
      }
    }
  }
`;

const StatsContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  position: relative;
  z-index: 1;

  @media (max-width: 968px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(1, 1fr);
    max-width: 280px;
  }
`;

const StatCard = styled(motion.div)`
  background: ${({ theme }) => `linear-gradient(${theme.card}, ${theme.card}) padding-box,
    linear-gradient(135deg, ${theme.primary}50, transparent) border-box`};
  padding: 1rem;
  border-radius: 16px;
  box-shadow: ${({ theme }) => theme.shadowMd};
  text-align: center;
  border: 2px solid transparent;
  cursor: pointer;
  position: relative;
  backdrop-filter: blur(10px);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg,
      ${({ theme }) => theme.primary}10,
      ${({ theme }) => theme.secondary}10
    );
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &::after {
    content: '';
    position: absolute;
    inset: -50%;
    background: conic-gradient(
      from 0deg,
      transparent 0%,
      ${({ theme }) => theme.primary}20 25%,
      transparent 50%
    );
    animation: rotate 4s linear infinite;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-5px) scale(1.02);
    box-shadow: ${({ theme }) => `0 10px 20px ${theme.primary}20`};

    &::before {
      opacity: 1;
    }

    &::after {
      opacity: 1;
    }

    .icon {
      transform: scale(1.1) rotate(10deg);
    }
  }

  @keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .icon {
    font-size: 1.8rem;
    background: ${({ color }) => `linear-gradient(135deg, ${color}, ${color}80)`};
    color: white;
    width: 35px;
    height: 35px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 0.8rem;
    transition: all 0.3s ease;
    box-shadow: 0 4px 8px ${({ color }) => `${color}40`};
  }

  h3 {
    ${({ theme }) => theme.mixins.headingStyles};
    font-size: ${({ theme }) => theme.sizes.sm};
    color: ${({ theme }) => theme.text};
    margin-bottom: 0.4rem;
    font-weight: 600;
  }

  .number {
    ${({ theme }) => theme.mixins.headingStyles};
    font-size: ${({ theme }) => theme.sizes.xl};
    background: linear-gradient(135deg,
      ${({ color }) => color},
      ${({ color }) => `${color}80`}
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin: 0.4rem 0;
    font-weight: 700;
  }
`;

  const Counter = ({ end, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const controls = useAnimation();

  useEffect(() => {
    let startTime = null;
    let rafId;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      
      if (progress < 1) {
        rafId = requestAnimationFrame(animate);
      }
    };

    controls.start({ scale: [0.5, 1], opacity: [0, 1] });
    rafId = requestAnimationFrame(animate);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [end, duration, controls]);

  return (
    <motion.span
      animate={controls}
      transition={{ type: "spring", stiffness: 100 }}
    >
      {count.toLocaleString()}+
    </motion.span>
  );
};

const FunFact = () => {
    
        const stats = [
          { icon: "😊", title: "Happy Clients", value: 10, color: "#FF6B6B" },
          { icon: "✓", title: "Projects Done", value: 15, color: "#4ECDC4" },
          { icon: "⏰", title: "Work Hours", value: 1000, color: "#45B7D1" },
          { icon: "👥", title: "Team Works", value: 5, color: "#96C93D" }
        ];

  return (
    <FunFactSection id="funfact">
      <SectionTitle
        initial={{ opacity: 0, y: -20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <motion.h2
          whileHover={{ scale: 1.05 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          Fun Facts
        </motion.h2>
      </SectionTitle>
      <StatsContainer>
        {stats.map((stat, index) => (
          <StatCard
            key={index}
            color={stat.color}
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ 
              duration: 0.5,
              delay: index * 0.1,
              type: "spring",
              stiffness: 100
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="icon">{stat.icon}</div>
            <h3>{stat.title}</h3>
            <div className="number">
              <Counter end={stat.value} />
            </div>
          </StatCard>
        ))}
      </StatsContainer>
    </FunFactSection>
  );
};

export default FunFact;
